import { useT } from '@/i18n';
import { isEmbedded, postToParent } from '@/lib/embed';

/**
 * Return affordance after a completion.
 *
 * - Mobile (non-embed): navigates the browser to the opener's `return` deep link, used verbatim
 *   (the real app scheme is `fixitapp://bozp-return`, built mobile-side via
 *   `Linking.createURL('bozp-return')`). The player treats `return` as OPAQUE — it echoes whatever
 *   the opener supplied and never hard-codes or validates the scheme.
 * - Web embed (`embed=web`): there is no mobile deep link to follow inside the iframe, so render a
 *   "Hotovo" button that asks the framing parent to close the frame via an allowlist-gated
 *   `{ type: 'bozp:close' }` postMessage instead of navigating to the `fixitapp://` scheme.
 */
export function ReturnButton({
  returnUrl,
  embed = null,
  parentOrigin = null,
  variant = 'ghost',
}: {
  returnUrl: string | null;
  embed?: string | null;
  parentOrigin?: string | null;
  variant?: 'primary' | 'ghost';
}) {
  const t = useT();
  const className = `hb-btn ${variant === 'primary' ? 'hb-btn--primary' : 'hb-btn--ghost'}`;

  // Web iframe AND mobile in-app WebView both close via the embed message —
  // there is no browser to navigate away from inside either host.
  if (isEmbedded(embed)) {
    return (
      <button
        type="button"
        data-testid="embed-close"
        onClick={() => postToParent(embed, parentOrigin, { type: 'bozp:close' })}
        className={className}
      >
        {t('Done')}
      </button>
    );
  }

  if (!returnUrl) return null;
  return (
    <a href={returnUrl} className={className}>
      {t('Return to FixIt')}
    </a>
  );
}
